import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Shield, Plus, Edit2, Trash2, Users, User, Search, ChevronDown, ChevronRight, X, Briefcase, UserPlus, RefreshCw, Key } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

// Comprehensive ERP Permission Categories
const PERMISSION_CATEGORIES = {
  'HRMS - Employee Management': [
    { id: 'employees.view', label: 'View Employees', description: 'View employee list and profiles' },
    { id: 'employees.create', label: 'Create Employees', description: 'Add new employees' },
    { id: 'employees.edit', label: 'Edit Employees', description: 'Modify employee details' },
    { id: 'employees.delete', label: 'Delete Employees', description: 'Remove employees' },
    { id: 'employees.export', label: 'Export Employee Data', description: 'Export to Excel/PDF' },
  ],
  'HRMS - Attendance': [
    { id: 'attendance.view_own', label: 'View Own Attendance', description: 'View personal attendance' },
    { id: 'attendance.view_team', label: 'View Team Attendance', description: 'View reportees attendance' },
    { id: 'attendance.view_all', label: 'View All Attendance', description: 'View organization attendance' },
    { id: 'attendance.checkin', label: 'Mark Attendance', description: 'Check in/out' },
    { id: 'attendance.approve', label: 'Approve Regularization', description: 'Approve attendance corrections' },
  ],
  'HRMS - Leave Management': [
    { id: 'leaves.view_own', label: 'View Own Leaves', description: 'View personal leave balance' },
    { id: 'leaves.view_team', label: 'View Team Leaves', description: 'View reportees leaves' },
    { id: 'leaves.view_all', label: 'View All Leaves', description: 'View all leave requests' },
    { id: 'leaves.apply', label: 'Apply for Leave', description: 'Submit leave requests' },
    { id: 'leaves.approve', label: 'Approve Leaves', description: 'Approve/reject leave requests' },
  ],
  'HRMS - Payroll & Performance': [
    { id: 'payroll.view_own', label: 'View Own Payslips', description: 'View personal salary' },
    { id: 'payroll.view_all', label: 'View All Payroll', description: 'View organization payroll' },
    { id: 'performance.view_own', label: 'View Own Performance', description: 'View personal reviews' },
    { id: 'performance.conduct_review', label: 'Conduct Reviews', description: 'Review reportees' },
  ],
  'Finance & Accounting': [
    { id: 'finance.dashboard', label: 'Finance Dashboard', description: 'View financial overview' },
    { id: 'finance.collections', label: 'Manage Collections', description: 'Record payments' },
    { id: 'accounting.vouchers', label: 'Create Vouchers', description: 'Create journal entries' },
    { id: 'accounting.ledgers', label: 'Manage Ledgers', description: 'View/create ledgers' },
  ],
  'Inventory & TRC': [
    { id: 'inventory.view', label: 'View Inventory', description: 'View stock levels' },
    { id: 'inventory.manage', label: 'Manage Inventory', description: 'Stock adjustments' },
    { id: 'trc.dashboard', label: 'TRC Dashboard', description: 'View TRC overview' },
    { id: 'trc.grading', label: 'Device Grading', description: 'Grade devices' },
  ],
  'Sales & Franchisee': [
    { id: 'sales.b2c', label: 'B2C Sales', description: 'Consumer sales' },
    { id: 'sales.b2b', label: 'B2B Sales', description: 'Business sales' },
    { id: 'franchisee.view', label: 'View Franchisees', description: 'View franchisee list' },
    { id: 'franchisee.manage', label: 'Manage Franchisees', description: 'Edit franchisee details' },
  ],
  'Administration': [
    { id: 'admin.users', label: 'Manage Users', description: 'User accounts' },
    { id: 'admin.roles', label: 'Manage Roles', description: 'Role permissions' },
    { id: 'admin.settings', label: 'System Settings', description: 'Global settings' },
    { id: 'hr.settings', label: 'HR Settings', description: 'HR configuration' },
  ],
};

function RoleManagement({ user, onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('roles');
  const [roles, setRoles] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Role form state
  const [showRoleForm, setShowRoleForm] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [roleFormData, setRoleFormData] = useState({
    name: '', description: '', permissions: []
  });
  const [submitting, setSubmitting] = useState(false);
  
  // User form state
  const [showUserForm, setShowUserForm] = useState(false);
  const [userFormData, setUserFormData] = useState({
    first_name: '', last_name: '', email: '', mobile_number: '',
    role: 'Employee', department: '', designation: '', password: ''
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [rolesRes, usersRes, employeesRes] = await Promise.allSettled([
        axios.get(`${API}/api/roles`, { headers }),
        axios.get(`${API}/api/users`, { headers }),
        axios.get(`${API}/api/employees`, { headers })
      ]);

      setRoles(rolesRes.status === 'fulfilled' ? (rolesRes.value.data || []) : []);
      
      let combined = [];
      if (usersRes.status === 'fulfilled') {
        const users = usersRes.value.data.users || usersRes.value.data || [];
        combined = [...combined, ...users.map(u => ({ ...u, source: 'users' }))];
      }
      if (employeesRes.status === 'fulfilled') {
        const emps = employeesRes.value.data.employees || employeesRes.value.data || [];
        const existingMobiles = new Set(combined.map(u => u.mobile_number));
        const newEmps = emps.filter(e => !existingMobiles.has(e.mobile_number));
        combined = [...combined, ...newEmps.map(e => ({ ...e, source: 'employees' }))];
      }
      setAllUsers(combined);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Statistics
  const stats = {
    totalRoles: roles.length,
    totalUsers: allUsers.length,
    activeUsers: allUsers.filter(u => u.is_active !== false).length,
    systemRoles: roles.filter(r => r.is_system_role).length
  };

  // Role handlers
  const handleRoleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      if (selectedRole) {
        await axios.put(`${API}/api/roles/${selectedRole.id}`, roleFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Role updated successfully!');
      } else {
        await axios.post(`${API}/api/roles`, roleFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Role created successfully!');
      }
      resetRoleForm();
      fetchAllData();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditRole = (role) => {
    setSelectedRole(role);
    setRoleFormData({
      name: role.name || role.role_name || '',
      description: role.description || '',
      permissions: role.permissions || []
    });
    setShowRoleForm(true);
  };

  const handleDeleteRole = async (roleId, roleName) => {
    if (!window.confirm(`Delete role "${roleName}"?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/roles/${roleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Role deleted successfully!');
      fetchAllData();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetRoleForm = () => {
    setRoleFormData({ name: '', description: '', permissions: [] });
    setSelectedRole(null);
    setShowRoleForm(false);
  };

  const togglePermission = (permId) => {
    setRoleFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permId)
        ? prev.permissions.filter(p => p !== permId)
        : [...prev.permissions, permId]
    }));
  };

  const toggleCategory = (category) => {
    const perms = PERMISSION_CATEGORIES[category].map(p => p.id);
    const allSelected = perms.every(p => roleFormData.permissions.includes(p));
    setRoleFormData(prev => ({
      ...prev,
      permissions: allSelected
        ? prev.permissions.filter(p => !perms.includes(p))
        : [...new Set([...prev.permissions, ...perms])]
    }));
  };

  // User handlers
  const handleUserSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/employees`, {
        ...userFormData,
        employee_id: `EMP${Date.now()}`,
        status: 'Active',
        joining_date: new Date().toISOString().split('T')[0]
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User created successfully!');
      setShowUserForm(false);
      setUserFormData({
        first_name: '', last_name: '', email: '', mobile_number: '',
        role: 'Employee', department: '', designation: '', password: ''
      });
      fetchAllData();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const getUsersForRole = (roleName) => {
    return allUsers.filter(u => u.role === roleName);
  };

  // Filter
  const filteredRoles = roles.filter(r => {
    if (!searchTerm) return true;
    const roleName = (r.name || r.role_name || '').toLowerCase();
    return roleName.includes(searchTerm.toLowerCase()) ||
           (r.description || '').toLowerCase().includes(searchTerm.toLowerCase());
  });

  const filteredUsers = allUsers.filter(u => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (u.first_name || '').toLowerCase().includes(search) ||
           (u.last_name || '').toLowerCase().includes(search) ||
           (u.email || '').toLowerCase().includes(search) ||
           (u.role || '').toLowerCase().includes(search);
  });

  const getStatusBadgeColor = (isSystem) => {
    return isSystem ? 'bg-blue-500 text-white' : 'bg-green-500 text-white';
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>🛡️ Role Management</h1>
            <p className="text-gray-600 mt-2">Manage roles, permissions, and user access</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchAllData}
              disabled={loading}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => { resetRoleForm(); setShowRoleForm(true); }}
              className="px-6 py-3 rounded-lg text-white font-medium flex items-center gap-2"
              style={{backgroundColor: '#2b3380'}}
            >
              <Plus className="w-5 h-5" />
              Create Role
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full" style={{backgroundColor: '#e8eaf6'}}>
              <Shield className="w-6 h-6" style={{color: '#2b3380'}} />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{color: '#2b3380'}}>{stats.totalRoles}</p>
              <p className="text-sm text-gray-500">Total Roles</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-green-100">
              <Users className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{stats.totalUsers}</p>
              <p className="text-sm text-gray-500">Total Users</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-blue-100">
              <User className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600">{stats.activeUsers}</p>
              <p className="text-sm text-gray-500">Active Users</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-purple-100">
              <Key className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-600">{stats.systemRoles}</p>
              <p className="text-sm text-gray-500">System Roles</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('roles')}
              className={`px-6 py-3 font-medium flex items-center gap-2 ${activeTab === 'roles' ? 'border-b-2' : 'text-gray-500'}`}
              style={activeTab === 'roles' ? {borderColor: '#2b3380', color: '#2b3380'} : {}}
            >
              <Shield className="w-4 h-4" /> Roles ({roles.length})
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`px-6 py-3 font-medium flex items-center gap-2 ${activeTab === 'users' ? 'border-b-2' : 'text-gray-500'}`}
              style={activeTab === 'users' ? {borderColor: '#2b3380', color: '#2b3380'} : {}}
            >
              <Users className="w-4 h-4" /> All Users ({allUsers.length})
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={activeTab === 'roles' ? "Search roles..." : "Search users by name, email, or role..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {activeTab === 'users' && (
              <button
                onClick={() => setShowUserForm(true)}
                className="px-4 py-2 border rounded-lg flex items-center gap-2 hover:bg-gray-50"
                style={{borderColor: '#2b3380', color: '#2b3380'}}
              >
                <UserPlus className="w-4 h-4" /> Create User
              </button>
            )}
          </div>
        </div>

        {/* ROLES TAB */}
        {activeTab === 'roles' && (
          <>
            {loading ? (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
                <p className="mt-3 text-gray-500">Loading roles...</p>
              </div>
            ) : filteredRoles.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <Shield className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-xl font-medium text-gray-500 mb-2">No roles found</p>
                <p className="text-gray-400">
                  {searchTerm ? 'Try different search terms' : 'Click "Create Role" to add one'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredRoles.map((role) => {
                  const roleName = role.name || role.role_name || 'Unknown';
                  const userCount = getUsersForRole(roleName).length;
                  return (
                    <div 
                      key={role.id} 
                      className="bg-white rounded-lg shadow-md p-6 border-l-4 hover:shadow-lg transition-shadow"
                      style={{borderLeftColor: role.is_system_role ? '#3b82f6' : '#10b981'}}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="p-2 bg-gray-100 rounded-lg">
                              <Shield className="w-5 h-5" style={{color: '#2b3380'}} />
                            </span>
                            <h3 className="text-xl font-bold" style={{color: '#2b3380'}}>{roleName}</h3>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(role.is_system_role)}`}>
                              {role.is_system_role ? 'SYSTEM' : 'CUSTOM'}
                            </span>
                            <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                              {userCount} users
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditRole(role)}
                            className="p-2 hover:bg-blue-50 rounded text-blue-600"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          {!role.is_system_role && (
                            <button
                              onClick={() => handleDeleteRole(role.id, roleName)}
                              className="p-2 hover:bg-red-50 rounded text-red-600"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {role.description && (
                        <p className="text-gray-700 mb-3">{role.description}</p>
                      )}
                      
                      <div className="flex flex-wrap justify-between items-center text-sm text-gray-500 pt-3 border-t">
                        <div className="flex items-center gap-4">
                          <span className="flex items-center gap-1">
                            <Key className="w-4 h-4" />
                            <strong>{role.permissions?.length || 0}</strong> permissions
                          </span>
                        </div>
                        <div className="flex gap-2 mt-2 md:mt-0">
                          <button
                            onClick={() => handleEditRole(role)}
                            className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-xs font-medium"
                          >
                            Manage Permissions
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* USERS TAB */}
        {activeTab === 'users' && (
          <>
            {loading ? (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
                <p className="mt-3 text-gray-500">Loading users...</p>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-xl font-medium text-gray-500 mb-2">No users found</p>
                <p className="text-gray-400">
                  {searchTerm ? 'Try different search terms' : 'Click "Create User" to add one'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredUsers.map((u, idx) => (
                  <div 
                    key={u.id || idx} 
                    className="bg-white rounded-lg shadow-md p-6 border-l-4 hover:shadow-lg transition-shadow"
                    style={{borderLeftColor: u.is_active !== false ? '#10b981' : '#ef4444'}}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <User className="w-5 h-5 text-gray-500" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold" style={{color: '#2b3380'}}>
                              {u.first_name || u.name || 'N/A'} {u.last_name || ''}
                            </h3>
                            <p className="text-sm text-gray-500">{u.employee_id || u.id}</p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${u.is_active !== false ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                            {u.is_active !== false ? 'ACTIVE' : 'INACTIVE'}
                          </span>
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                            {u.role || 'N/A'}
                          </span>
                        </div>
                      </div>
                      <button 
                        onClick={() => navigate(`/employee/${u.id || u.employee_id}`)}
                        className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-xs font-medium"
                      >
                        View Profile
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                      <div><strong>Email:</strong> {u.email || 'N/A'}</div>
                      <div><strong>Mobile:</strong> {u.mobile_number || 'N/A'}</div>
                      <div><strong>Department:</strong> {u.department || 'N/A'}</div>
                      <div><strong>Designation:</strong> {u.designation || 'N/A'}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Role Form Modal */}
        {showRoleForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
              <div className="p-6 border-b flex justify-between items-center">
                <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>
                  {selectedRole ? '✏️ Edit Role' : '🛡️ Create New Role'}
                </h2>
                <button onClick={resetRoleForm} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleRoleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Role Name *</label>
                    <input
                      type="text"
                      value={roleFormData.name}
                      onChange={(e) => setRoleFormData({...roleFormData, name: e.target.value})}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Description</label>
                    <input
                      type="text"
                      value={roleFormData.description}
                      onChange={(e) => setRoleFormData({...roleFormData, description: e.target.value})}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Brief description of this role..."
                    />
                  </div>
                </div>

                {/* Permissions */}
                <div className="border rounded-lg">
                  <div className="p-4 bg-gray-50 border-b font-semibold flex justify-between items-center">
                    <span>Permissions ({roleFormData.permissions.length} selected)</span>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {Object.entries(PERMISSION_CATEGORIES).map(([category, perms]) => {
                      const selected = perms.filter(p => roleFormData.permissions.includes(p.id)).length;
                      const isExpanded = expandedCategories[category];
                      return (
                        <div key={category} className="border-b last:border-b-0">
                          <div 
                            className="p-4 flex items-center gap-3 hover:bg-gray-50 cursor-pointer"
                            onClick={() => setExpandedCategories(prev => ({...prev, [category]: !prev[category]}))}
                          >
                            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            <input
                              type="checkbox"
                              checked={selected === perms.length}
                              onChange={(e) => { e.stopPropagation(); toggleCategory(category); }}
                              className="w-4 h-4 rounded"
                            />
                            <span className="font-medium flex-1">{category}</span>
                            <span className="text-xs bg-gray-100 px-2 py-1 rounded">{selected}/{perms.length}</span>
                          </div>
                          {isExpanded && (
                            <div className="pl-12 pr-4 pb-4 grid grid-cols-2 gap-2">
                              {perms.map(perm => (
                                <label key={perm.id} className="flex items-start gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={roleFormData.permissions.includes(perm.id)}
                                    onChange={() => togglePermission(perm.id)}
                                    className="w-4 h-4 mt-0.5 rounded"
                                  />
                                  <div>
                                    <div className="text-sm font-medium">{perm.label}</div>
                                    <div className="text-xs text-gray-500">{perm.description}</div>
                                  </div>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="flex gap-3 pt-6">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-3 text-white rounded-lg font-medium flex items-center gap-2"
                    style={{backgroundColor: submitting ? '#9e9e9e' : '#2b3380'}}
                  >
                    <Shield className="w-5 h-5" />
                    {submitting ? 'Saving...' : (selectedRole ? 'Update Role' : 'Create Role')}
                  </button>
                  <button
                    type="button"
                    onClick={resetRoleForm}
                    className="px-6 py-3 border rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* User Form Modal */}
        {showUserForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>👤 Create New User</h2>
                  <button onClick={() => setShowUserForm(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleUserSubmit}>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">First Name *</label>
                        <input
                          type="text"
                          value={userFormData.first_name}
                          onChange={(e) => setUserFormData({...userFormData, first_name: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Last Name *</label>
                        <input
                          type="text"
                          value={userFormData.last_name}
                          onChange={(e) => setUserFormData({...userFormData, last_name: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Email *</label>
                        <input
                          type="email"
                          value={userFormData.email}
                          onChange={(e) => setUserFormData({...userFormData, email: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Mobile *</label>
                        <input
                          type="tel"
                          value={userFormData.mobile_number}
                          onChange={(e) => setUserFormData({...userFormData, mobile_number: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Role *</label>
                        <select
                          value={userFormData.role}
                          onChange={(e) => setUserFormData({...userFormData, role: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          {roles.map(r => {
                            const rName = r.name || r.role_name || 'Unknown';
                            return <option key={r.id} value={rName}>{rName}</option>;
                          })}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Password *</label>
                        <input
                          type="password"
                          value={userFormData.password}
                          onChange={(e) => setUserFormData({...userFormData, password: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Department</label>
                        <input
                          type="text"
                          value={userFormData.department}
                          onChange={(e) => setUserFormData({...userFormData, department: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Designation</label>
                        <input
                          type="text"
                          value={userFormData.designation}
                          onChange={(e) => setUserFormData({...userFormData, designation: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-700">
                        <strong>Note:</strong> The user will be able to login using their mobile number and password.
                      </p>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="px-6 py-3 text-white rounded-lg font-medium flex items-center gap-2"
                        style={{backgroundColor: submitting ? '#9e9e9e' : '#2b3380'}}
                      >
                        <UserPlus className="w-5 h-5" />
                        {submitting ? 'Creating...' : 'Create User'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowUserForm(false)}
                        className="px-6 py-3 border rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default RoleManagement;
